import { useCallback } from 'react';
import isNil from 'lodash/isNil';

import {
  UniversalFilterTextValue,
  UniversalFilterTextModes,
  UniversalFilterTextFilterState,
  UniversalFilterTextOnChange
} from '../../UniversalFilterText.types';

interface UniversalFilterTextFilterStateOptions {
  onChange: UniversalFilterTextOnChange;
}

function useUniversalFilterTextFilterState({
  onChange
}: UniversalFilterTextFilterStateOptions) {
  // All modes and options available in handleApplyFilterState
  // must be implemented in handleParseFilterValue too
  const handleApplyFilterState = useCallback<
    (filterState: UniversalFilterTextFilterState) => void
  >(
    (filterState) => {
      if (filterState?.mode === UniversalFilterTextModes.IS_NOT_EMPTY) {
        onChange({ isNotNull: true });
        return;
      }

      if (filterState?.mode === UniversalFilterTextModes.IS_EMPTY) {
        onChange({ isNull: true });
        return;
      }

      const text = filterState?.text;

      if (filterState?.mode === UniversalFilterTextModes.CONTAINS) {
        onChange(text ? { ilike: text } : null);
        return;
      }

      if (filterState?.mode === UniversalFilterTextModes.IS) {
        onChange(text ? { eq: text } : null);
        return;
      }

      if (filterState?.mode === UniversalFilterTextModes.IS_NOT) {
        onChange(text ? { notEq: text } : null);
        return;
      }

      if (filterState?.mode === UniversalFilterTextModes.STARTS_WITH) {
        onChange(text ? { ilikeStart: text } : null);
        return;
      }

      if (filterState?.mode === UniversalFilterTextModes.ENDS_WITH) {
        onChange(text ? { ilikeEnd: text } : null);
        return;
      }

      onChange();
    },
    [onChange]
  );

  const handleParseFilterValue = useCallback<
    (
      filterValue: UniversalFilterTextValue
    ) => UniversalFilterTextFilterState | undefined
  >((filterValue) => {
    if (isNil(filterValue)) {
      return undefined;
    }

    if (filterValue.isNotNull === true) {
      return {
        mode: UniversalFilterTextModes.IS_NOT_EMPTY
      };
    }

    if (filterValue.isNull === true) {
      return {
        mode: UniversalFilterTextModes.IS_EMPTY
      };
    }

    if (filterValue.ilike) {
      return {
        mode: UniversalFilterTextModes.CONTAINS,
        text: filterValue.ilike
      };
    }

    if (filterValue.eq) {
      return {
        mode: UniversalFilterTextModes.IS,
        text: filterValue.eq
      };
    }

    if (filterValue.notEq) {
      return {
        mode: UniversalFilterTextModes.IS_NOT,
        text: filterValue.notEq
      };
    }

    if (filterValue.ilikeStart) {
      return {
        mode: UniversalFilterTextModes.STARTS_WITH,
        text: filterValue.ilikeStart
      };
    }

    if (filterValue.ilikeEnd) {
      return {
        mode: UniversalFilterTextModes.ENDS_WITH,
        text: filterValue.ilikeEnd
      };
    }

    return undefined;
  }, []);

  return { handleApplyFilterState, handleParseFilterValue };
}

export default useUniversalFilterTextFilterState;
